import React, { useState } from 'react';
import { Box, Dialog, IconButton, TextField, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ApiButton from '../../UIControls/api-button/api-button';
import { useSendFeedback } from '../handout/use-send-feedback';

const TOPICS = ['Illustration', 'Text', 'Idea', 'Technical Issue', 'Other'];

export default function SuggestHandoutDialog({ open, onClose }) {
    const sendFeedback = useSendFeedback();
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [message, setMessage] = useState('');
    const isSubmitDisabled = selectedTopic === null || message.trim() === '';

    const handleDialogClose = () => {
        setSelectedTopic(null);
        setMessage('');
        onClose();
    };

    const submitFeedback = async () => {
        if (isSubmitDisabled) {
            return;
        }

        await sendFeedback(selectedTopic, message);
    };

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '30px',
                    width: { xs: '354px', md: '895px' },
                    maxWidth: { xs: '354px', md: '895px' },
                    m: { xs: 0, md: 2 },
                    p: { xs: '24px', md: '30px' },
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    maxHeight: { xs: 'calc(100vh - 24px)', md: 'calc(100vh - 32px)' },
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <IconButton
                onClick={handleDialogClose}
                sx={{
                    position: 'absolute',
                    right: { xs: '12px', md: '20px' },
                    top: { xs: '12px', md: '20px' },
                    color: '#000453',
                }}
            >
                <CloseIcon />
            </IconButton>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: { xs: 0, md: '6px' } }}>
                <Typography
                    sx={{
                        color: '#000453',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: { xs: '26px', md: '42px' },
                        lineHeight: { xs: '26px', md: '42px' },
                        letterSpacing: { xs: '-1.04px', md: '-1.68px' },
                        mb: { xs: '12px', md: '24px' },
                        pr: '42px',
                    }}
                >
                    Suggest a handout
                </Typography>

                <Typography
                    sx={{
                        color: '#616063',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: { xs: '14px', md: '24px' },
                        lineHeight: { xs: '18px', md: '32px' },
                        letterSpacing: { xs: '-0.56px', md: '-0.96px' },
                        mb: { xs: '18px', md: '24px' },
                        whiteSpace: 'pre-line',
                    }}
                >
                    <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                        {'Your suggestions help us grow and improve our library so it stays relevant, up-to-date and useful for veterinary teams and pet owners alike. Sharing as much detail as you can helps us understand your needs and review your idea more effectively.'}
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                        {'Your suggestions help us grow and improve our library so it stays relevant,\nup-to-date and useful for veterinary teams and pet owners alike. Sharing as much detail as you can helps us understand your needs and review your idea more effectively.'}
                    </Box>
                </Typography>

                <Typography
                    sx={{
                        color: '#616063',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: { xs: '16px', md: '24px' },
                        lineHeight: { xs: '16px', md: '24px' },
                        letterSpacing: { xs: '-0.32px', md: '-0.96px' },
                        mb: '12px',
                    }}
                >
                    Topic
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: { xs: '20px', md: '40px' }, mt: { xs: 0, md: '21px' } }}>
                    {TOPICS.map((topic) => {
                        const selected = selectedTopic === topic;
                        return (
                            <Button
                                key={topic}
                                onClick={() => setSelectedTopic(topic)}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: { xs: '139.588px', md: '224px' },
                                    height: { xs: '32.155px', md: '56px' },
                                    minHeight: { xs: '32.155px', md: '56px' },
                                    px: { xs: '18.695px', md: '30px' },
                                    py: { xs: '10.469px', md: '16.8px' },
                                    gap: { xs: '8px', md: '15.806px' },
                                    border: {
                                        xs: selected ? 'none' : '1.246px solid #FF669B',
                                        md: selected ? 'none' : '2px solid #FF669B',
                                    },
                                    background: selected ? '#000453' : '#FFFFFF',
                                    color: selected ? '#FFFFFF' : '#FF669B',
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 500,
                                    fontSize: { xs: '11.217px', md: '18px' },
                                    lineHeight: { xs: '11.217px', md: '18px' },
                                    letterSpacing: { xs: '-0.2243px', md: '-0.36px' },
                                    textShadow: {
                                        xs: '0 0 6.979px rgba(35, 7, 88, 0.10)',
                                        md: '0 0 11.2px rgba(35, 7, 88, 0.10)',
                                    },
                                    '&:hover': {
                                        background: selected ? '#000453' : '#FFFFFF',
                                    },
                                }}
                            >
                                {topic}
                            </Button>
                        );
                    })}
                </Box>

                <Typography
                    sx={{
                        color: '#616063',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: { xs: '16px', md: '24px' },
                        lineHeight: { xs: '16px', md: '24px' },
                        letterSpacing: { xs: '-0.32px', md: '-0.96px' },
                        mb: { xs: '12px', md: 0 },
                    }}
                >
                    Message
                </Typography>

                <TextField
                    multiline
                    rows={6}
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder="Describe the handout or topic you'd like us to create..."
                    fullWidth
                    sx={{
                        mb: { xs: '20px', md: '24px' },
                        mt: { xs: 0, md: '21px' },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: { xs: '16px', md: '20px' },
                            fontFamily: 'Inter, sans-serif',
                            fontSize: { xs: '16px', md: '20px' },
                            letterSpacing: { xs: '-0.32px', md: '-0.8px' },
                            color: '#616063',
                            height: { xs: 'auto', md: '208px' },
                            '& fieldset': { borderColor: '#E5E5E5', borderWidth: '1.5px' },
                            '&:hover fieldset': { borderColor: '#E5E5E5' },
                            '&.Mui-focused fieldset': { borderColor: '#FF669B' },
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: '#A0A0A0',
                            opacity: 0.9,
                        },
                    }}
                />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0, pt: { xs: 0, md: '8px' } }}>
                <ApiButton
                    apiCall={submitFeedback}
                    buttonVariant="pink"
                    successTitle="Feedback submitted"
                    successMessage="Your feedback has been submitted successfully."
                    errorTitle="Feedback error"
                    errorMessage="There was a problem submitting your feedback. Please try again."
                    disabled={isSubmitDisabled}
                    onSuccessConfirm={handleDialogClose}
                    buttonLabel="Submit feedback"
                    buttonStyle={{
                        borderRadius: { xs: '100px', md: '30px' },
                        width: { xs: '298px', md: '300px' },
                        maxWidth: { xs: '100%', md: '300px' },
                        height: { xs: '44px', md: '56px' },
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '14px', md: '20px' },
                        lineHeight: { xs: '14px', md: '20px' },
                        letterSpacing: { xs: '-0.28px', md: '-0.4px' },
                        backgroundColor: '#FF669B',
                        backgroundImage: 'none',
                        '&:hover': { backgroundColor: '#FF85B0' },
                    }}
                />
            </Box>
        </Dialog>
    );
}
